/* ===================================================================
   useActivity() -- per-day activity heatmap + gamification for the
   Dashboard contribution graph and the streak/badges/goal panel.

   Binds to GET /api/v1/activity (engine/api/v1/app.py -> analytics/activity.py)
   through the typed api client (src/lib/api.ts). Disabled until a Bearer rr_
   token exists. `days` sets the trailing window (server default 364 = 52
   weeks, max 366). Every figure is real -- empty days are count 0 and a
   metric with no signal is 0/None; the screen renders that truthfully.
   =================================================================== */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api, type ActivityResponse, type AdminTenantDashboard } from '../lib/api';
import { useApiToken } from './useApiToken';
import { useAdminView } from '../admin/adminViewContext';
import { adminDashboardKey } from './useAdminTenantDashboard';
import { qk } from './queryKeys';

export interface UseActivityParams {
  /** Trailing day cells (server default 364). */
  days?: number;
  /** Force-disable even if a token exists. */
  enabled?: boolean;
}

export function useActivity(
  params: UseActivityParams = {},
): UseQueryResult<ActivityResponse, Error> {
  const { days, enabled } = params;
  const hasToken = Boolean(useApiToken());
  const admin = useAdminView();

  // OPTION A: in an operator read-only view, select the activity slice out of
  // the single admin dashboard bundle (one cached fetch shared across panels).
  // The slice can be null (honest null when the activity read soft-failed); the
  // Dashboard already renders that truthfully (days ?? []).
  return useQuery({
    queryKey: admin ? adminDashboardKey(admin.clientId) : qk.activity(days),
    queryFn: ({ signal }) =>
      admin
        ? (api.getTenantDashboard(admin.clientId, signal) as Promise<unknown>)
        : (api.getActivity(days, signal) as Promise<unknown>),
    select: admin
      ? (d) => (d as AdminTenantDashboard).activity as ActivityResponse
      : undefined,
    enabled: (admin ? true : hasToken) && enabled !== false,
    staleTime: 60_000,
  }) as UseQueryResult<ActivityResponse, Error>;
}
